import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { UserCircleIcon } from "@heroicons/react/20/solid"
import {
  type Contact,
  createContact,
  updateContact,
} from "@src/app/(app)/(auth)/contacts/actions"
import Button from "@src/components/Button"
import QrScannerView from "@src/components/DefuseSDK/components/QrScannerView"
import { classifyScannedAddress } from "@src/components/DefuseSDK/features/contacts/utils/classifyScannedAddress"
import { detectNetworkFromAddress } from "@src/components/DefuseSDK/features/contacts/utils/detectNetworkFromAddress"
import { stripPaymentUriPrefix } from "@src/components/DefuseSDK/features/contacts/utils/stripPaymentUriPrefix"
import {
  useQrScanner,
  useQrScannerPermissionGate,
} from "@src/components/DefuseSDK/hooks/useQrScanner"
import { parseScannedRecipient } from "@src/components/DefuseSDK/utils/parseScannedRecipient"
import { getScannableChains } from "@src/components/DefuseSDK/utils/scanner/getScannableChains"
import { Dialog } from "@src/components/Dialog"
import ErrorMessage from "@src/components/ErrorMessage"
import ListItem from "@src/components/ListItem"
import TokenIconPlaceholder from "@src/components/TokenIconPlaceholder"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import useSearchNetworks from "@src/hooks/useFilterNetworks"
import { ScanIcon, WalletSolidIcon } from "@src/icons"
import { parseNearcomAddress } from "@src/shared/utils/nearcomAddress"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import {
  getNearIntentsOption,
  INTERNAL_NETWORK_NAME,
  isIntentsOption,
  type NetworkOption,
} from "../../constants/blockchains"
import type { ContactNetwork } from "../../features/contacts/utils/contactNetwork"
import { emitEvent } from "../../services/emitter"
import type { SupportedChainName } from "../../types/base"
import { assetNetworkAdapter } from "../../utils/adapters"
import { allAvailableChains } from "../../utils/blockchain"
import { validateAddress } from "../../utils/validateAddress"
import { InternalBadge } from "../Network/InternalBadge"
import { NetworkList } from "../Network/NetworkList"
import SearchBar from "../SearchBar"
import ModalNoResults from "./ModalNoResults"

type FormData = {
  address: string
  name: string
  blockchain: ContactNetwork | null
}

type ModalContactProps = {
  open: boolean
  contact?: Contact | null
  defaultValues?: Partial<FormData>
  onSuccess?: (contact: {
    name: string
    address: string
    blockchain: ContactNetwork
  }) => void
  onClose: () => void
  onCloseAnimationEnd?: () => void
}

const ModalAddEditContact = ({
  open,
  onClose,
  onCloseAnimationEnd,
  contact,
  defaultValues: defaultValuesProp,
  onSuccess,
}: ModalContactProps) => {
  const [selectNetworkOpen, setSelectNetworkOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const { state } = useConnectWallet()
  const queryClient = useQueryClient()
  const userAddress = state.address
  // ChainType enum values match AuthMethod values, so we can safely cast
  const chainType = state.chainType as AuthMethod | undefined

  const isEditing = Boolean(contact)

  const defaultValues = useMemo<FormData>(
    () =>
      selectInternalNetworkForPrefixedAddress({
        name: defaultValuesProp?.name ?? "",
        address: defaultValuesProp?.address ?? "",
        blockchain: defaultValuesProp?.blockchain ?? null,
      }),
    [defaultValuesProp]
  )

  const formValues = useMemo<FormData | undefined>(
    () =>
      open
        ? contact
          ? selectInternalNetworkForPrefixedAddress({
              name: contact.name,
              address: contact.address,
              blockchain: contact.blockchain,
            })
          : selectInternalNetworkForPrefixedAddress({
              name: defaultValuesProp?.name ?? "",
              address: defaultValuesProp?.address ?? "",
              blockchain: defaultValuesProp?.blockchain ?? null,
            })
        : undefined,
    [contact, open, defaultValuesProp]
  )

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues,
    values: formValues,
    resetOptions: {
      keepErrors: true,
      keepIsSubmitted: false,
    },
  })

  register("blockchain", {
    validate: (value) => value !== null || "Select a network for this contact.",
  })

  const address = watch("address")
  const blockchain = watch("blockchain")
  // A recognized internal address determines its network unambiguously.
  const isNetworkLocked = parseNearcomAddress(address) != null

  const availableNetworks: Record<string, NetworkOption> = useMemo(
    () => ({ ...getNearIntentsOption(), ...allAvailableChains() }),
    []
  )
  const availableChainsForScan = useMemo(() => getScannableChains(), [])
  const filteredNetworks = useSearchNetworks({
    networks: availableNetworks,
    searchValue,
  })
  const intentsNetworkInfo =
    Object.values(filteredNetworks).find(isIntentsOption)
  const orderedFilteredNetworks = useMemo(() => {
    const nearNetworkKey = assetNetworkAdapter.near
    const nearNetwork = filteredNetworks[nearNetworkKey]
    if (nearNetwork == null) return filteredNetworks

    return {
      [nearNetworkKey]: nearNetwork,
      ...Object.fromEntries(
        Object.entries(filteredNetworks).filter(
          ([key]) => key !== nearNetworkKey
        )
      ),
    }
  }, [filteredNetworks])

  const selectedNetwork = blockchain
  const networkData = useMemo(
    () =>
      blockchain == null
        ? null
        : blockchain === "near_intents"
          ? getNearIntentsOption().intents
          : availableNetworks[assetNetworkAdapter[blockchain]],
    [blockchain, availableNetworks]
  )

  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const requestScannerPermission = useQrScannerPermissionGate()

  // Provenance: tracks the network that was most recently set by an
  // auto-detect in this modal session. Used by the scan handler to
  // decide whether the current network value is safe to override or
  // clear (see auto-detect rules above the scan handler). Reset on
  // modal close so each fresh add/edit session starts clean.
  const lastScanSetNetworkRef = useRef<ContactNetwork | null>(null)

  // If the parent swaps the `contact` prop while the modal is open
  // (e.g. navigating from one contact's edit view to another without
  // unmounting), drop any in-progress scanner state and the scan
  // provenance ref so neither leaks across contacts. Render-time
  // ref-compare is the React-recommended way to reset state on prop
  // change without an effect (and this file is intentionally
  // effect-free per the project's React patterns guidance).
  const lastSeenContactIdRef = useRef(contact?.contactId ?? null)
  const currentContactId = contact?.contactId ?? null
  if (lastSeenContactIdRef.current !== currentContactId) {
    lastSeenContactIdRef.current = currentContactId
    setIsScannerOpen(false)
    lastScanSetNetworkRef.current = null
  }

  const {
    videoRef: scannerVideoRef,
    error: scannerError,
    clearError: clearScannerError,
  } = useQrScanner({
    enabled: open && isScannerOpen,
    onScan: async (rawValue) => {
      const parsed = parseScannedRecipient(rawValue)
      const nearcomAddress = parseNearcomAddress(parsed)
      if (nearcomAddress != null) {
        const currentBlockchain = getValues("blockchain")
        if (
          currentBlockchain != null &&
          currentBlockchain !== "near_intents" &&
          lastScanSetNetworkRef.current !== currentBlockchain
        ) {
          return {
            accepted: false,
            error: "Please enter a valid address for the selected blockchain.",
          }
        }

        setValue("address", nearcomAddress.displayAddress, {
          shouldValidate: true,
        })
        setValue("blockchain", "near_intents", { shouldValidate: true })
        lastScanSetNetworkRef.current = "near_intents"
        setIsScannerOpen(false)
        return { accepted: true }
      }

      const normalized = stripPaymentUriPrefix(parsed).trim()

      // Reject scans that didn't yield a usable address.
      if (
        normalized.length === 0 ||
        /\s/.test(normalized) ||
        /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(normalized) // any unsupported scheme
      ) {
        return {
          accepted: false,
          error: "Couldn't read a wallet address from this QR.",
        }
      }

      // Classify against the available chain validators. Differentiate
      // "we recognise this format but can't transact to it" (e.g. Zcash
      // shielded `zs1…` or unified `u1…`) from "we have no idea what this
      // is" — the user deserves different copy for each.
      const classification = classifyScannedAddress(
        normalized,
        availableChainsForScan
      )
      if (classification.kind === "recognized-unsupported") {
        return {
          accepted: false,
          error: `Unfortunately, we do not currently support ${classification.formatName} addresses.`,
        }
      }
      if (classification.kind === "unrecognized") {
        return {
          accepted: false,
          error: "Couldn't read a wallet address from this QR.",
        }
      }

      setValue("address", normalized, { shouldValidate: true })

      // Auto-detect rules:
      //
      // 1. Provenance: only override / clear the network when the current
      //    value was set by a previous auto-detect in this modal session
      //    (tracked via lastScanSetNetworkRef). A user's manual pick — or
      //    a value pre-filled when editing a saved contact — is preserved
      //    so a fresh scan can't silently clobber it.
      //
      // 2. Soft-fix for detector false-negatives (e.g. a Solana pubkey that
      //    starts with `G` and is excluded from Solana detection by the
      //    prefix-based shortcut): when detection fails BUT the prior
      //    scan-set network's validator still accepts the new address,
      //    keep the network rather than clearing it.
      //
      // 3. Note on EVM addresses: a bare `0x` + 40 hex address is detected
      //    as `eth` by design — that format is shared by every EVM chain
      //    (Arbitrum, Base, Optimism, etc.) and we can't disambiguate from
      //    the address alone. Worst case is a wrong-network mismatch; the
      //    user always sees the network field after the scan and can
      //    correct it before save. The same private key controls all EVM
      //    chains so funds aren't lost — just on the wrong chain.
      const detected = detectNetworkFromAddress(normalized)
      const detectedBlockchainEnum =
        detected != null ? assetNetworkAdapter[detected] : null
      const detectedAvailable =
        detected != null &&
        detectedBlockchainEnum != null &&
        availableNetworks[detectedBlockchainEnum] != null

      const currentBlockchain = getValues("blockchain")
      const currentSetByPriorScan =
        currentBlockchain != null &&
        lastScanSetNetworkRef.current === currentBlockchain

      if (detectedAvailable && detected != null) {
        if (currentBlockchain == null || currentSetByPriorScan) {
          setValue("blockchain", detected, { shouldValidate: true })
          lastScanSetNetworkRef.current = detected
        }
        // else: manual / saved — preserve.
      } else if (currentSetByPriorScan && currentBlockchain != null) {
        // Detection failed and the current value came from a prior scan.
        // Apply the soft-fix before clearing.
        const stillValidates =
          currentBlockchain !== "near_intents" &&
          validateAddress(normalized, currentBlockchain)
        if (stillValidates) {
          // Detector false-negative; the prior scan-set network is still
          // correct for this address. Keep it; refresh provenance.
          lastScanSetNetworkRef.current = currentBlockchain
        } else {
          setValue("blockchain", null, { shouldValidate: true })
          lastScanSetNetworkRef.current = null
        }
      }
      // else (detect failed and current is manual/saved): preserve.

      setIsScannerOpen(false)
      return { accepted: true }
    },
  })

  // Matches ModalSelectRecipient: also clear any scanner error so reopening
  // lands on a clean slate (ModalSelectRecipient.tsx:233-236).
  const closeScanner = useCallback(() => {
    setIsScannerOpen(false)
    clearScannerError()
  }, [clearScannerError])

  const handleOpenScanner = useCallback(async () => {
    clearScannerError()
    if (!(await requestScannerPermission())) return
    setIsScannerOpen(true)
  }, [clearScannerError, requestScannerPermission])

  const onSubmit = async (data: FormData) => {
    if (!data.blockchain) {
      return
    }

    if (!state.isAuthorized) {
      setError("root.submit", {
        type: "server",
        message: "Please sign in again to save contacts.",
      })
      return
    }

    clearErrors("root.submit")

    try {
      if (isEditing && contact) {
        if (!contact.contactId) {
          setError("root.submit", {
            type: "server",
            message: "Contact ID is missing. Please try again.",
          })
          return
        }

        const result = await updateContact({
          contactId: contact.contactId,
          name: data.name,
          address: data.address,
          blockchain: data.blockchain,
          userAddress,
          chainType,
        })

        if (!result.ok) {
          setError("root.submit", {
            type: "server",
            message: result.error,
          })
          return
        }

        emitEvent("contact_updated", { blockchain: data.blockchain })
      } else {
        const result = await createContact({
          name: data.name,
          address: data.address,
          blockchain: data.blockchain,
          userAddress,
          chainType,
        })

        if (!result.ok) {
          setError("root.submit", {
            type: "server",
            message: result.error,
          })
          return
        }

        emitEvent("contact_created", { blockchain: data.blockchain })
      }

      await queryClient.invalidateQueries({ queryKey: ["contacts"] })

      onSuccess?.({
        name: data.name,
        address: data.address,
        blockchain: data.blockchain,
      })
      onClose()
    } catch (error) {
      setError("root.submit", {
        type: "server",
        message:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
      })
    }
  }

  const getModalTitle = useMemo(() => {
    if (isScannerOpen) return "Scan recipient QR code"
    if (selectNetworkOpen) return "Select network"
    return isEditing ? "Edit contact" : "Create contact"
  }, [isScannerOpen, selectNetworkOpen, isEditing])

  const onChangeNetwork = useCallback(
    (network: SupportedChainName) => {
      setValue("blockchain", network, {
        shouldValidate: true,
      })
      // Manual pick — even if `network` happens to equal what the scan
      // set — relinquishes scan provenance. Without this, a later scan
      // could still override or clear the value because
      // `lastScanSetNetworkRef.current === currentBlockchain` keeps
      // returning true.
      lastScanSetNetworkRef.current = null
      setSelectNetworkOpen(false)
      setSearchValue("")
    },
    [setValue]
  )

  const onIntentsSelect = useCallback(() => {
    setValue("blockchain", "near_intents", {
      shouldValidate: true,
    })
    lastScanSetNetworkRef.current = null
    setSelectNetworkOpen(false)
    setSearchValue("")
  }, [setValue])

  const handleDialogClose = useCallback(() => {
    if (selectNetworkOpen) {
      setSelectNetworkOpen(false)
      return
    }

    if (isScannerOpen) {
      closeScanner()
      return
    }

    onClose()
  }, [onClose, isScannerOpen, closeScanner, selectNetworkOpen])

  return (
    <Dialog
      title={getModalTitle}
      open={open}
      onClose={handleDialogClose}
      onCloseAnimationEnd={() => {
        onCloseAnimationEnd?.()
        reset()
        setSelectNetworkOpen(false)
        closeScanner()
        lastScanSetNetworkRef.current = null
      }}
      back={
        isScannerOpen
          ? closeScanner
          : selectNetworkOpen
            ? () => setSelectNetworkOpen(false)
            : undefined
      }
      header={
        selectNetworkOpen ? (
          <SearchBar
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClear={() => setSearchValue("")}
            placeholder="Search networks"
            autoFocus
          />
        ) : undefined
      }
      preserveHeight={selectNetworkOpen}
    >
      {isScannerOpen ? (
        <QrScannerView
          videoRef={scannerVideoRef}
          error={scannerError}
          onCancel={closeScanner}
        />
      ) : selectNetworkOpen ? (
        <>
          {Object.keys(filteredNetworks).length === 0 ? (
            <ModalNoResults
              text="No networks found"
              handleSearchClear={() => setSearchValue("")}
            />
          ) : (
            <div className="space-y-1">
              {intentsNetworkInfo && (
                <ListItem
                  onClick={onIntentsSelect}
                  highlight={selectedNetwork === "near_intents"}
                >
                  {intentsNetworkInfo.icon}
                  <ListItem.Content>
                    <ListItem.Title>
                      {INTERNAL_NETWORK_NAME} user
                    </ListItem.Title>
                    <ListItem.Subtitle>
                      For contacts using this app
                    </ListItem.Subtitle>
                  </ListItem.Content>
                </ListItem>
              )}

              <NetworkList
                networkOptions={orderedFilteredNetworks}
                selectedNetwork={selectedNetwork}
                onChangeNetwork={onChangeNetwork}
              />
            </div>
          )}
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="space-y-2">
            <div>
              <label
                className={clsx(
                  "focus-within:-outline-offset-2 hover:-outline-offset-2 flex cursor-text items-center gap-3 rounded-3xl bg-white p-3 focus-within:outline-2 hover:outline-2 dark:bg-gray-850",
                  errors.name
                    ? "-outline-offset-2 outline-2 outline-red-500 focus-within:outline-red-500 hover:outline-red-500 dark:outline-red-500/50 dark:hover:outline-red-500 dark:focus-within:outline-red-500"
                    : "-outline-offset-1 outline-1 outline-gray-200 focus-within:outline-gray-400 hover:outline-gray-400 dark:outline-white/5"
                )}
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                  <UserCircleIcon className="size-5 text-gray-500 dark:text-gray-300" />
                </div>
                <div className="flex-1">
                  <span className="sr-only">Name</span>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Enter name"
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    className="block w-full font-semibold text-base/none text-gray-700 outline-none placeholder:text-gray-400 dark:text-white"
                    {...register("name", {
                      required: "Enter the name of the contact.",
                    })}
                  />
                </div>
              </label>
              {errors.name && (
                <ErrorMessage className="mt-1 mb-4">
                  {errors.name.message}
                </ErrorMessage>
              )}
            </div>

            <div>
              <div>
                <label
                  className={clsx(
                    "focus-within:-outline-offset-2 hover:-outline-offset-2 flex flex-1 cursor-text items-center gap-1.5 rounded-3xl bg-white p-3 pr-2 focus-within:outline-2 hover:outline-2 md:pr-3 dark:bg-gray-850",
                    errors.address
                      ? "-outline-offset-2 outline-2 outline-red-500 focus-within:outline-red-500 hover:outline-red-500 dark:outline-red-500/50 dark:hover:outline-red-500 dark:focus-within:outline-red-500"
                      : "-outline-offset-1 outline-1 outline-gray-200 focus-within:outline-gray-400 hover:outline-gray-400 dark:outline-white/5"
                  )}
                >
                  <div className="flex flex-1 items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                      <WalletSolidIcon className="size-5 text-gray-500 dark:text-gray-300" />
                    </div>
                    <div className="flex-1">
                      <span className="sr-only">Address</span>
                      <input
                        id="contact-address"
                        type="text"
                        placeholder="Enter address"
                        autoComplete="off"
                        data-form-type="other"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        className="block w-full font-semibold text-base/none text-gray-700 outline-none placeholder:text-gray-400 dark:text-white"
                        {...register("address", {
                          required: "Enter the address of the contact.",
                          onChange: (event) => {
                            if (
                              parseNearcomAddress(event.target.value) != null
                            ) {
                              setValue("blockchain", "near_intents", {
                                shouldValidate: true,
                              })
                              lastScanSetNetworkRef.current = "near_intents"
                            }
                          },
                        })}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenScanner}
                    className="flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                    aria-label="Scan QR code"
                  >
                    <ScanIcon className="size-5" />
                  </button>
                </label>
              </div>
              {errors.address && (
                <ErrorMessage className="mt-1 mb-4">
                  {errors.address.message}
                </ErrorMessage>
              )}
            </div>

            <div>
              <button
                type="button"
                disabled={isNetworkLocked}
                onClick={
                  isNetworkLocked ? undefined : () => setSelectNetworkOpen(true)
                }
                className={clsx(
                  "focus-visible:-outline-offset-2 hover:not-disabled:-outline-offset-2 flex w-full items-center gap-3 rounded-3xl bg-white p-3 text-left hover:not-disabled:outline-2 focus-visible:outline-2 dark:bg-gray-850",
                  isNetworkLocked && "cursor-default",
                  errors.blockchain
                    ? "-outline-offset-2 outline-2 outline-red-500 hover:outline-red-500 focus-visible:outline-red-500 dark:outline-red-500/50 dark:focus-visible:outline-red-500 dark:hover:outline-red-500"
                    : "-outline-offset-1 outline-1 outline-gray-200 hover:not-disabled:outline-gray-400 focus-visible:outline-gray-400 dark:outline-white/5"
                )}
              >
                <span className="flex flex-1 items-center gap-3">
                  <div className="size-10 shrink-0">
                    {networkData?.icon ?? (
                      <TokenIconPlaceholder className="size-10" />
                    )}
                  </div>
                  <span className="flex flex-1 items-center gap-2">
                    <span
                      className={clsx(
                        "flex-1 font-semibold text-base/none",
                        networkData
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-400"
                      )}
                    >
                      {networkData?.label ?? "Select network"}
                    </span>
                    {blockchain === "near_intents" && <InternalBadge />}
                  </span>
                </span>
              </button>
              {errors.blockchain && (
                <ErrorMessage className="mt-1 mb-4">
                  {errors.blockchain.message}
                </ErrorMessage>
              )}
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="xl"
            fullWidth
            className="mt-5"
            loading={isSubmitting}
          >
            {isEditing ? "Update contact" : "Save contact"}
          </Button>

          {errors.root?.submit?.message && (
            <ErrorMessage className="mt-3 text-balance text-center">
              {errors.root.submit.message}
            </ErrorMessage>
          )}
        </form>
      )}
    </Dialog>
  )
}

function selectInternalNetworkForPrefixedAddress(
  values: FormData
): FormData {
  return parseNearcomAddress(values.address) == null
    ? values
    : { ...values, blockchain: "near_intents" }
}

export default ModalAddEditContact
